// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import {
  type AssetCreationUploadResult,
  uploadAssetCreation,
  uploadAssetCreationResponseSchema,
} from '@/asset/api/upload-asset-creation';
import { CsvUpload } from '@/asset/components/asset-creation-upload/csv-upload';
import { Results } from '@/asset/components/asset-creation-upload/results';
import { Stepper } from '@/asset/components/asset-creation-upload/stepper';
import { TemplateDownload } from '@/asset/components/asset-creation-upload/template-download';
import type {
  ExpandedAssetsState,
  Step,
} from '@/asset/components/asset-creation-upload/types';
import { useSupportError } from '@/hooks/use-support-error';
import { Box } from '@mui/material';
import { captureException, withScope } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ChangeEvent, useCallback, useRef, useState } from 'react';

interface AssetCreationUploadProps {
  onClose: () => void;
}

export function AssetCreationUpload(props: AssetCreationUploadProps) {
  const queryClient = useQueryClient();
  const { showSupportError } = useSupportError();
  const [currentStep, setCurrentStep] = useState<Step>('download-template');
  const [file, setFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] =
    useState<AssetCreationUploadResult | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const [uploadLineCount, setUploadLineCount] = useState<number | undefined>(
    undefined
  );
  const [uploadHeaders, setUploadHeaders] = useState<string[]>([]);
  const [expandedAssets, setExpandedAssets] = useState<ExpandedAssetsState>({
    created: false,
    failed: false,
    structureErrors: false,
  });
  const [hasFatalError, setHasFatalError] = useState(false);

  const clearFile = () => {
    setFile(null);
    setUploadLineCount(undefined);
    setUploadHeaders([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onClose = () => {
    props.onClose();
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSelectFile = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        setFile(selectedFile);
        setUploadErrorMessage(null);

        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter((line) => line.trim() !== '');
          setUploadLineCount(Math.max(0, lines.length - 1));
          if (lines.length > 0) {
            const headers = lines[0]
              .split(',')
              .map((h) => h.trim().toLowerCase());
            setUploadHeaders(headers);
          }
        };
        reader.readAsText(selectedFile);
      }
    },
    []
  );

  const handleUpload = useCallback(async () => {
    if (!file) return;

    setIsUploading(true);
    setUploadErrorMessage(null);

    try {
      const formData = new FormData();
      formData.append('data', file);

      const result = await uploadAssetCreation(formData);
      clearFile();
      setUploadResult(result);
      setCurrentStep('results');
      queryClient.invalidateQueries({ queryKey: ['asset'] });
    } catch (error) {
      clearFile();
      const axiosError = error as AxiosError;

      // First try to parse as a response with validation errors (rowErrors, structureErrors)
      const knownError = uploadAssetCreationResponseSchema.safeParse(
        axiosError.response?.data
      );
      if (knownError.success) {
        // Handled error - show validation results to user
        setUploadResult(knownError.data.data);
        setUploadErrorMessage(null);
        setCurrentStep('results');
        return;
      }

      // Could not parse as validation errors - use generic error handler
      const apiError = handleAPIError(axiosError);

      // Curated codes from ERROR_MESSAGES are the handled set: show
      // inline, skip Sentry. Anything that falls through to the support
      // fallback is unhandled and must fire Sentry.
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setUploadErrorMessage(curated);
        return;
      }
      if (!apiError.sentToSentry) {
        withScope((scope) => {
          scope.setContext('upload', {
            fileSize: file?.size,
            lineCount: uploadLineCount,
          });
          captureException(error);
        });
      }
      setHasFatalError(true);
      showSupportError('An error occurred while creating assets.');
    } finally {
      setIsUploading(false);
    }
  }, [file, queryClient, showSupportError, uploadLineCount]);

  const handleToggleExpanded = useCallback(
    (section: keyof ExpandedAssetsState) => {
      setExpandedAssets((prev) => ({
        ...prev,
        [section]: !prev[section],
      }));
    },
    []
  );

  const handleStartNew = useCallback(() => {
    setCurrentStep('download-template');
    setFile(null);
    setUploadResult(null);
    setUploadErrorMessage(null);
    setUploadLineCount(undefined);
    setUploadHeaders([]);
    setExpandedAssets({
      created: false,
      failed: false,
      structureErrors: false,
    });
  }, []);

  const handleBack = useCallback(() => {
    if (currentStep === 'upload-csv') {
      setCurrentStep('download-template');
      setHasFatalError(false);
    } else if (currentStep === 'results') {
      setCurrentStep('upload-csv');
    }
  }, [currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep === 'download-template') {
      setCurrentStep('upload-csv');
    }
  }, [currentStep]);

  return (
    <Box sx={{ p: 3 }}>
      <Stepper currentStep={currentStep} />

      {currentStep === 'download-template' && (
        <TemplateDownload onBack={onClose} onNext={handleNext} />
      )}

      {currentStep === 'upload-csv' && (
        <CsvUpload
          uploadErrorMessage={uploadErrorMessage}
          file={file}
          isUploading={isUploading}
          onSelectFile={handleSelectFile}
          onUpload={handleUpload}
          onBack={handleBack}
          onClose={onClose}
          hasFatalError={hasFatalError}
          uploadLineCount={uploadLineCount}
          uploadHeaders={uploadHeaders}
        />
      )}

      {currentStep === 'results' && (
        <Results
          uploadResult={uploadResult}
          expandedAssets={expandedAssets}
          onToggleExpanded={handleToggleExpanded}
          onBack={handleBack}
          onStartNew={handleStartNew}
          onClose={onClose}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </Box>
  );
}
